import React, { useState } from 'react';
import {
  Box,
  Container,
  Paper,
  Typography,
  TextField,
  Button,
  Alert,
  Link as MuiLink,
  CircularProgress,
} from '@mui/material';
import { Email, ArrowBack } from '@mui/icons-material';
import { Link } from 'react-router-dom';
import api from '../services/api';

const ForgotPasswordPage: React.FC = () => {
  const [email, setEmail] = useState('');
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);
  const [error, setError] = useState('');

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    try {
      const response = await api.post('/password/request-reset', { email });

      if (response.data.success) {
        setSuccess(true);
      }
    } catch (err: any) {
      setError(err.response?.data?.detail || 'Failed to send reset email. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <Box
      sx={{
        minHeight: '100vh',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
        py: 4,
      }}
    >
      <Container maxWidth="sm">
        <Paper
          elevation={10}
          sx={{
            p: 4,
            borderRadius: 2,
          }}
        >
          {!success ? (
            <>
              {/* Header */}
              <Box sx={{ textAlign: 'center', mb: 4 }}>
                <Email sx={{ fontSize: 60, color: '#667eea', mb: 2 }} />
                <Typography variant="h4" fontWeight="bold" gutterBottom>
                  Forgot Password?
                </Typography>
                <Typography variant="body1" color="text.secondary">
                  Enter your email address and we'll send you a link to reset your password.
                </Typography>
              </Box>

              {/* Error Alert */}
              {error && (
                <Alert severity="error" sx={{ mb: 3 }}>
                  {error}
                </Alert>
              )}

              {/* Form */}
              <form onSubmit={handleSubmit}>
                <TextField
                  fullWidth
                  label="Email Address"
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  required
                  disabled={loading}
                  sx={{ mb: 3 }}
                  autoFocus
                  placeholder="you@example.com"
                />

                <Button
                  type="submit"
                  fullWidth
                  variant="contained"
                  size="large"
                  disabled={loading}
                  sx={{
                    py: 1.5,
                    mb: 2,
                    background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                    '&:hover': {
                      background: 'linear-gradient(135deg, #5568d3 0%, #653b8a 100%)',
                    },
                  }}
                >
                  {loading ? (
                    <CircularProgress size={24} color="inherit" />
                  ) : (
                    'Send Reset Link'
                  )}
                </Button>

                <Box sx={{ textAlign: 'center' }}>
                  <MuiLink
                    component={Link}
                    to="/login"
                    sx={{
                      display: 'inline-flex',
                      alignItems: 'center',
                      textDecoration: 'none',
                      color: '#667eea',
                      '&:hover': { textDecoration: 'underline' },
                    }}
                  >
                    <ArrowBack sx={{ fontSize: 16, mr: 0.5 }} />
                    Back to Login
                  </MuiLink>
                </Box>
              </form>
            </>
          ) : (
            <>
              {/* Success Message */}
              <Box sx={{ textAlign: 'center' }}>
                <Email sx={{ fontSize: 80, color: '#4caf50', mb: 2 }} />
                <Typography variant="h4" fontWeight="bold" gutterBottom color="success.main">
                  Check Your Email
                </Typography>
                <Typography variant="body1" color="text.secondary" paragraph>
                  If an account exists for <strong>{email}</strong>, we've sent a password reset link.
                </Typography>

                <Alert severity="info" sx={{ mb: 3, textAlign: 'left' }}>
                  <Typography variant="body2" fontWeight="bold" gutterBottom>
                    What's next?
                  </Typography>
                  <Typography variant="body2">
                    1. Check your email inbox (and spam folder)<br />
                    2. Click the password reset link in the email<br />
                    3. Create a new password<br />
                    4. Log in with your new password
                  </Typography>
                </Alert>

                <Alert severity="warning" sx={{ mb: 3, textAlign: 'left' }}>
                  <Typography variant="body2">
                    <strong>Didn't receive the email?</strong><br />
                    • Check your spam/junk folder<br />
                    • Make sure you entered the correct email<br />
                    • Wait a few minutes and check again<br />
                    • The link expires in 1 hour
                  </Typography>
                </Alert>

                <Box sx={{ display: 'flex', gap: 2, justifyContent: 'center' }}>
                  <Button
                    variant="outlined"
                    onClick={() => {
                      setSuccess(false);
                      setEmail('');
                    }}
                  >
                    Try Another Email
                  </Button>
                  <Button
                    variant="contained"
                    component={Link}
                    to="/login"
                    sx={{
                      background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                    }}
                  >
                    Back to Login
                  </Button>
                </Box>
              </Box>
            </>
          )}
        </Paper>

        {/* Footer */}
        <Box sx={{ mt: 3, textAlign: 'center' }}>
          <Typography variant="body2" sx={{ color: 'white' }}>
            © 2025 Dure Technologies Pvt Ltd. All rights reserved.
          </Typography>
        </Box>
      </Container>
    </Box>
  );
};

export default ForgotPasswordPage;
